<template>
  <div class="wrap">
    <header class="top">
      <div>
        <b>Manager Portal</b>
        <small> — {{ user?.username }} ({{ user?.role }})</small>
      </div>
      <div class="actions">
        <button @click="refreshAll">Refresh</button>
        <button class="logout" @click="logout">Logout</button>
        <button @click="$router.push('/manager/flows')">Flows</button>
      </div>
    </header>

    <main class="grid">
      <!-- Agents -->
      <section class="card">
        <div class="cardHead">
          <h3>Agents</h3>
        </div>

        <div class="agents">
          <div v-for="a in agents" :key="a.id" class="agent">
            <div class="row">
              <b>{{ a.username }}</b>
              <span class="pill" :class="a.state">{{ a.state }}</span>
            </div>
            <small>Open chats: {{ a.open_chats }}</small>
          </div>
        </div>
      </section>

      <!-- Conversations -->
      <section class="card">
        <div class="cardHead">
          <h3>Conversations</h3>

          <div class="tabs">
            <button class="tab" :class="{on: filter==='OPEN'}" @click="setFilter('OPEN')">OPEN</button>
            <button class="tab" :class="{on: filter==='QUEUED'}" @click="setFilter('QUEUED')">QUEUED</button>
            <button class="tab" :class="{on: filter==='CLOSED'}" @click="setFilter('CLOSED')">CLOSED</button>
          </div>
        </div>

        <div class="convs">
          <div v-for="c in conversations" :key="c.conversation_id" class="conv">
            <div class="row">
              <b>#{{ c.conversation_id }}</b>
              <span class="pill" :class="c.status">{{ c.status }}</span>
              <span class="muted" style="margin-left:auto;">
                {{ c.assigned_username ? ("@" + c.assigned_username) : "Unassigned" }}
              </span>
            </div>

            <small>
              {{ c.form_json }}
              {{ c.visitor_name || "Visitor" }} • {{ c.visitor_phone || "" }}
            </small>

            <div class="preview" v-if="c.last_message">{{ c.last_message }}</div>

            <div class="row" style="margin-top:10px; gap:8px; flex-wrap: wrap;">
              <button class="secondary" @click="openReadOnlyChat(c)">
                View Chat
              </button>

              <select v-model="assignTo[c.conversation_id]" :disabled="filter==='CLOSED'">
                <option value="">Assign to…</option>
                <option v-for="a in agents" :key="a.id" :value="a.id">
                  {{ a.username }} ({{ a.state }})
                </option>
              </select>

              <button
                @click="assign(c.conversation_id)"
                :disabled="filter==='CLOSED' || !assignTo[c.conversation_id]"
              >
                Assign
              </button>

              <button
                class="danger"
                @click="closeConv(c.conversation_id)"
                :disabled="c.status==='CLOSED'"
              >
                Close
              </button>
            </div>
          </div>

          <div v-if="!conversations.length" class="muted" style="padding:10px;">
            No conversations found.
          </div>
        </div>
      </section>
    </main>

    <!-- READ ONLY CHAT MODAL -->
    <div v-if="chatModalOpen" class="modalOverlay" @click.self="closeReadOnlyChat">
      <div class="modal">
        <div class="modalHead">
          <div>
            <h3 style="margin:0;">Conversation #{{ activeConversation?.conversation_id }}</h3>
            <small class="muted">
              {{ activeConversation?.visitor_name || "Visitor" }}
              <span v-if="activeConversation?.visitor_phone"> • {{ activeConversation.visitor_phone }}</span>
              <span v-if="activeConversation?.assigned_username"> • @{{ activeConversation.assigned_username }}</span>
            </small>
          </div>

          <button class="logout" @click="closeReadOnlyChat">Close</button>
        </div>

        <div class="messages">
          <div v-if="chatLoading" class="muted">Loading messages...</div>

          <div v-else-if="!chatMessages.length" class="muted">No messages found.</div>

          <div
            v-for="m in chatMessages"
            :key="m.id"
            class="msg"
            :class="messageClass(m)"
          >
            <div class="msgMeta">
              <b>{{ messageSender(m) }}</b>
              <small class="muted">{{ formatTime(m.created_at) }}</small>
            </div>
            <div class="msgBody">{{ m.body }}</div>
          </div>
        </div>

        <!-- READ ONLY FOOTER -->
        <div class="readOnlyBar">
          Read only mode — managers can view ongoing chat but cannot reply from here.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { auth } from "@/services/auth";

const API_BASE = "https://sudarshan.fonetricks.com";
// const API_BASE = "http://localhost:3000";

const router = useRouter();
const user = ref(auth.user);

const agents = ref([]);
const conversations = ref([]);
const filter = ref("OPEN");
const assignTo = ref({});

const chatModalOpen = ref(false);
const activeConversation = ref(null);
const chatMessages = ref([]);
const chatLoading = ref(false);

function logout() {
  auth.clear();
  router.push("/");
}

async function apiGet(path) {
  const r = await fetch(API_BASE + path, {
    headers: { Authorization: "Bearer " + auth.token },
  });

  if (r.status === 401) {
    logout();
    throw new Error("401");
  }

  const data = await r.json();
  if (!data.ok) throw new Error(data.message || "API_ERROR");
  return data;
}




async function apiPost(path, body) {
  const r = await fetch(API_BASE + path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + auth.token,
    },
    body: JSON.stringify(body || {}),
  });

  if (r.status === 401) {
    logout();
    throw new Error("401");
  }

  const data = await r.json();
  if (!data.ok) throw new Error(data.message || "API_ERROR");
  return data;
}

async function loadAgents() {
  const data = await apiGet("/api/manager/agents");
  agents.value = data.agents || [];
}

async function loadConversations() {
  const data = await apiGet(`/api/manager/conversations?filter=${filter.value}`);
  conversations.value = data.conversations || [];
}

async function refreshAll() {
  await loadAgents();
  await loadConversations();
}

async function setFilter(v) {
  filter.value = v;
  await loadConversations();
}

async function assign(conversationId) {
  const agentId = assignTo.value[conversationId];
  if (!agentId) return;

  await apiPost(`/api/manager/conversation/${conversationId}/assign`, {
    agentId: Number(agentId),
  });

  await refreshAll();
}

async function closeConv(conversationId) {
  await apiPost(`/api/manager/conversation/${conversationId}/close`, {});
  await refreshAll();
}

async function openReadOnlyChat(conv) {
  activeConversation.value = conv;
  chatModalOpen.value = true;
  chatLoading.value = true;
  chatMessages.value = [];

  try {
    const data = await apiGet(`/api/manager/conversation/${conv.conversation_id}/messages`);
    chatMessages.value = data.messages || [];
  } catch (e) {
    console.error("Failed to load messages", e);
    chatMessages.value = [];
  } finally {
    chatLoading.value = false;
  }
}

function closeReadOnlyChat() {
  chatModalOpen.value = false;
  activeConversation.value = null;
  chatMessages.value = [];
}

function messageSender(m) {
  if (m.sender_type === "AGENT") return m.sender_name || "Agent";
  if (m.sender_type === "VISITOR") return "Visitor";
  if (m.sender_type === "BOT") return "Bot";
  return m.sender_type || "System";
}

function messageClass(m) {
  if (m.sender_type === "AGENT") return "agentMsg";
  if (m.sender_type === "VISITOR") return "visitorMsg";
  if (m.sender_type === "BOT") return "botMsg";
  return "systemMsg";
}

function formatTime(v) {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return v;
  }
}

onMounted(async () => {
  if (!auth.token) return router.push("/");
  if (!["admin", "manager"].includes(auth.user?.role)) return router.push("/inbox");
  await refreshAll();
});
</script>

<style scoped>
.wrap { font-family: Arial; }
.top {
  display:flex; justify-content:space-between; align-items:center;
  padding: 12px 16px; border-bottom: 1px solid #eee;
}
.actions { display:flex; gap:8px; }
button { padding: 8px 10px; border: none; border-radius: 10px; background:#0b66d1; color:#fff; cursor:pointer; }
button.logout { background:#444; }
button.danger { background:#e74c3c; }
button.secondary { background:#6c757d; }

.grid { display:grid; grid-template-columns: 360px 1fr; gap: 12px; padding: 12px; }
.card { border:1px solid #eee; border-radius: 12px; overflow:hidden; }
.cardHead { padding: 10px 12px; border-bottom: 1px solid #eee; display:flex; justify-content:space-between; align-items:center; gap:12px; }
h3 { margin:0; }

.agents { padding: 12px; display:flex; flex-direction:column; gap:10px; max-height: calc(100vh - 110px); overflow:auto; }
.agent { padding: 10px; border:1px solid #eee; border-radius: 12px; background:#fafafa; }
.row { display:flex; align-items:center; gap:8px; }

.tabs { display:flex; gap:8px; }
.tab { background:#eee; color:#000; }
.tab.on { background:#0b66d1; color:#fff; }

.convs { padding: 12px; display:flex; flex-direction:column; gap:10px; max-height: calc(100vh - 110px); overflow:auto; }
.conv { padding: 10px; border:1px solid #eee; border-radius: 12px; }
.preview { margin-top:6px; font-size: 12px; color:#555; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

select { padding: 8px; border-radius: 10px; border:1px solid #ccc; }
.muted { color:#777; font-size: 12px; }

.pill { padding: 2px 8px; border-radius: 999px; border:1px solid #ddd; font-size: 11px; background:#f5f5f5; }
.pill.READY { background:#e9f6ee; border-color:#bfe6cc; }
.pill.LIVE { background:#4fc77d; border-color:#abe4be; color: #fafafa; }
.pill.AWAY { background:#fff5e6; border-color:#ffe0a3; }
.pill.OFFLINE { background:#f5f5f5; border-color:#ddd; }
.pill.OPEN { background:#e9f6ee; border-color:#bfe6cc; }
.pill.CLOSED { background:#f5f5f5; border-color:#ddd; }
.pill.QUEUED { background:#fff5e6; border-color:#ffe0a3; }

/* modal */
.modalOverlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.modal {
  width: min(900px, 92vw);
  height: min(80vh, 700px);
  background: #fff;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modalHead {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px;
  border-bottom: 1px solid #eee;
}

.messages {
  flex: 1;
  overflow: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #f8f9fb;
}

.msg {
  max-width: 75%;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #ddd;
  background: white;
}

.msgMeta {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}

.msgBody {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
}

.agentMsg {
  align-self: flex-end;
  background: #eaf3ff;
  border-color: #cfe0ff;
}

.visitorMsg {
  align-self: flex-start;
  background: #fff;
}

.botMsg {
  align-self: flex-start;
  background: #eefaf0;
  border-color: #cfead5;
}

.systemMsg {
  align-self: center;
  background: #f1f1f1;
  max-width: 90%;
}

.readOnlyBar {
  padding: 12px 14px;
  border-top: 1px solid #eee;
  background: #fafafa;
  color: #666;
  font-size: 13px;
}
</style>